import { Bookmark, BookmarkCheck, Brain, Clock, Compass, Dumbbell, Search, Shield, Utensils } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';


const AyahuascaPrepGuide = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCards, setFilteredCards] = useState([]);
  const [bookmarkedCards, setBookmarkedCards] = useState(() => {
    const saved = localStorage.getItem('ayahuasca-bookmarks');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [completedCards, setCompletedCards] = useState(() => {
    const saved = localStorage.getItem('ayahuasca-completed');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [showCompletedStash, setShowCompletedStash] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTimeline, setSelectedTimeline] = useState('all');
  const [highlightedCards, setHighlightedCards] = useState(new Set());
  const [isSearching, setIsSearching] = useState(false);

  const categories = {
    mental: { name: 'Mental', color: 'bg-green-100 text-green-800 border-green-200', icon: Brain },
    nutrition: { name: 'Nutrition', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Utensils },
    physical: { name: 'Physical', color: 'bg-red-100 text-red-800 border-red-200', icon: Dumbbell },
    safety: { name: 'Safety', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Shield },
    integration: { name: 'Integration', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Compass }
  };

  // All your cards data here...
  const cards = useMemo(() => [
    // Mental Preparation
    { id: 1, title: "Practice Meditation Daily", category: "mental", summary: "30 minutes daily focus training", timing: "Start 4 weeks before", priority: "high", keywords: ["meditate", "daily", "focus", "mind", "calm"] },
    { id: 2, title: "Set Clear Intentions", category: "mental", summary: "Focus on what needs healing, not how", timing: "1 week before", priority: "high", keywords: ["intention", "goal", "healing", "purpose"] },
    { id: 3, title: "Journal 5 Minutes Daily", category: "mental", summary: "Map your starting point and goals", timing: "Start 3 weeks before", priority: "medium", keywords: ["journal", "write", "thoughts", "goals"] },
    { id: 4, title: "Spend Time Alone in Nature", category: "mental", summary: "Face inner world without distractions", timing: "Start 2 weeks before", priority: "medium", keywords: ["alone", "nature", "solitude", "inner"] },
    { id: 5, title: "Practice Daily Prayer", category: "mental", summary: "Cultivate humility and gratitude", timing: "Start 2 weeks before", priority: "low", keywords: ["prayer", "humble", "grateful", "spiritual"] },
    { id: 56, title: "Listen to Mantras Daily", category: "mental", summary: "Sanskrit words to purify the mind", timing: "Start 2 weeks before", priority: "low", keywords: ["mantras", "sanskrit", "chant", "purify"] },
    { id: 6, title: "Listen to Calming Music Only", category: "mental", summary: "No aggressive or overstimulating sounds", timing: "2 weeks before", priority: "low", keywords: ["music", "calm", "aggressive", "peaceful"] },

    // Nutrition - Foods to Avoid
    { id: 7, title: "No Red Meat", category: "nutrition", summary: "Stop all beef, lamb, etc.", timing: "1 week before", priority: "high", keywords: ["red meat", "beef", "lamb", "meat"] },
    { id: 8, title: "No Pork", category: "nutrition", summary: "Complete pork restriction", timing: "2 weeks before", priority: "high", keywords: ["pork", "bacon", "ham", "pig"] },
    { id: 9, title: "No Dairy Products", category: "nutrition", summary: "Milk, cheese, yogurt, butter", timing: "1 week before", priority: "high", keywords: ["dairy", "milk", "cheese", "yogurt", "butter"] },
    { id: 10, title: "No Aged Cheese", category: "nutrition", summary: "High tyramine - dangerous with ayahuasca", timing: "1 week before", priority: "critical", keywords: ["aged cheese", "tyramine", "parmesan", "cheddar"] },
    { id: 11, title: "No Fermented Foods", category: "nutrition", summary: "Kimchi, sauerkraut, miso, tofu", timing: "1 week before", priority: "high", keywords: ["fermented", "kimchi", "sauerkraut", "miso", "tofu"] },
    { id: 12, title: "No Sugar", category: "nutrition", summary: "All refined sugars and sweets", timing: "1 week before", priority: "high", keywords: ["sugar", "sweet", "candy", "refined"] },
    { id: 13, title: "No Chocolate/Cacao", category: "nutrition", summary: "All chocolate products", timing: "1 week before", priority: "high", keywords: ["chocolate", "cacao", "cocoa"] },
    { id: 14, title: "No Caffeine", category: "nutrition", summary: "Coffee, tea, energy drinks", timing: "1 week before", priority: "high", keywords: ["caffeine", "coffee", "tea", "energy drinks"] },
    { id: 15, title: "No Alcohol", category: "nutrition", summary: "All alcoholic beverages", timing: "2 weeks before", priority: "critical", keywords: ["alcohol", "beer", "wine", "spirits", "drink"] },
    { id: 16, title: "No Spicy Foods", category: "nutrition", summary: "Hot peppers, spicy seasonings", timing: "2 weeks before", priority: "medium", keywords: ["spicy", "hot", "pepper", "chili"] },
    { id: 17, title: "No Salt", category: "nutrition", summary: "Minimize all salt and sodium", timing: "1 week before", priority: "medium", keywords: ["salt", "sodium", "salty"] },
    { id: 18, title: "No Ice/Cold Drinks", category: "nutrition", summary: "Room temperature only", timing: "2 weeks before", priority: "medium", keywords: ["ice", "cold", "frozen", "temperature"] },
    { id: 19, title: "No Carbonated Drinks", category: "nutrition", summary: "Sodas, sparkling water, energy drinks", timing: "1 week before", priority: "medium", keywords: ["carbonated", "soda", "sparkling", "fizzy"] },
    { id: 20, title: "No Processed Meats", category: "nutrition", summary: "Hot dogs, bacon, salami, pepperoni", timing: "1 week before", priority: "high", keywords: ["processed meat", "hot dogs", "bacon", "salami"] },
    { id: 21, title: "No Onions", category: "nutrition", summary: "All types of onions", timing: "1 week before", priority: "medium", keywords: ["onions", "onion"] },
    { id: 22, title: "No Garlic", category: "nutrition", summary: "Fresh or powdered garlic", timing: "1 week before", priority: "medium", keywords: ["garlic"] },
    { id: 23, title: "No Avocados", category: "nutrition", summary: "High tyramine when ripe", timing: "1 week before", priority: "medium", keywords: ["avocado", "avocados"] },
    { id: 44, title: "No Animal Fats", category: "nutrition", summary: "Lard, butter, animal-based fats", timing: "1 week before", priority: "medium", keywords: ["animal fats", "lard", "butter"] },
    { id: 45, title: "No Oils", category: "nutrition", summary: "Use olive/coconut oil very sparingly", timing: "1 week before", priority: "medium", keywords: ["oils", "olive oil", "coconut oil"] },
    { id: 46, title: "No Junk Foods", category: "nutrition", summary: "All processed junk food", timing: "1 week before", priority: "high", keywords: ["junk food", "processed", "fast food"] },
    { id: 47, title: "No Protein Supplements", category: "nutrition", summary: "Protein powders, extracts, shakes", timing: "1 week before", priority: "medium", keywords: ["protein powder", "supplements", "shakes", "protein"] },
    { id: 48, title: "No Yeast Products", category: "nutrition", summary: "Yeast extracts, Marmite, sourdough", timing: "1 week before", priority: "medium", keywords: ["yeast", "marmite", "sourdough", "bread"] },
    { id: 49, title: "No Overripe/Dried Fruits", category: "nutrition", summary: "Raisins, prunes, overripe bananas", timing: "1 week before", priority: "medium", keywords: ["dried fruit", "raisins", "prunes", "overripe"] },
    { id: 50, title: "No Soy Products", category: "nutrition", summary: "Soy sauce, tofu, tempeh, miso", timing: "1 week before", priority: "high", keywords: ["soy", "soy sauce", "tofu", "tempeh", "miso"] },
    { id: 51, title: "No Aged Meats", category: "nutrition", summary: "Dry sausages, pepperoni, salami", timing: "1 week before", priority: "high", keywords: ["aged meat", "sausage", "pepperoni", "salami"] },
    { id: 52, title: "No Meat Tenderizers", category: "nutrition", summary: "Meat prepared with tenderizers", timing: "1 week before", priority: "medium", keywords: ["meat tenderizer", "tenderizer"] },
    { id: 53, title: "No Fish Sauce", category: "nutrition", summary: "Fish sauce, shrimp sauce, teriyaki", timing: "1 week before", priority: "medium", keywords: ["fish sauce", "shrimp sauce", "teriyaki"] },
    { id: 54, title: "No Broad Beans", category: "nutrition", summary: "Fava beans and their pods", timing: "1 week before", priority: "medium", keywords: ["broad beans", "fava beans"] },
    { id: 55, title: "No Leeks", category: "nutrition", summary: "All types of leeks", timing: "1 week before", priority: "medium", keywords: ["leeks"] },
    { id: 69, title: "No Chicken/Poultry", category: "nutrition", summary: "Traditional diet is vegan - no meat", timing: "1 week before", priority: "high", keywords: ["chicken", "poultry", "turkey", "duck", "bird", "meat", "animal"] },

    // Nutrition - Allowed Foods
    { id: 24, title: "Eat Brown Rice", category: "nutrition", summary: "Safe whole grain option", timing: "Allowed during diet", priority: "low", keywords: ["brown rice", "rice", "grain"] },
    { id: 25, title: "Eat Quinoa", category: "nutrition", summary: "Excellent protein and grain", timing: "Allowed during diet", priority: "low", keywords: ["quinoa"] },
    { id: 26, title: "Eat Lentils", category: "nutrition", summary: "Great protein source", timing: "Allowed during diet", priority: "low", keywords: ["lentils", "protein"] },
    { id: 27, title: "Eat Beans", category: "nutrition", summary: "All types of beans", timing: "Allowed during diet", priority: "low", keywords: ["beans", "legumes"] },
    { id: 28, title: "Eat Fresh Vegetables", category: "nutrition", summary: "All except onions, garlic, leeks", timing: "Allowed during diet", priority: "low", keywords: ["vegetables", "veggie", "fresh"] },
    { id: 29, title: "Eat Fresh Fruits", category: "nutrition", summary: "Avoid overripe or dried fruits", timing: "Allowed during diet", priority: "low", keywords: ["fruits", "fresh fruit", "apple", "banana"] },
    { id: 30, title: "Drink Herbal Tea", category: "nutrition", summary: "Caffeine-free herbal teas only", timing: "Allowed during diet", priority: "low", keywords: ["herbal tea", "tea", "chamomile"] },
    { id: 31, title: "Eat Raw Nuts", category: "nutrition", summary: "Except peanuts, unsalted only", timing: "Allowed during diet", priority: "low", keywords: ["nuts", "raw nuts", "almonds", "walnuts"] },
    { id: 57, title: "Eat Wheat Berries", category: "nutrition", summary: "Whole wheat grains", timing: "Allowed during diet", priority: "low", keywords: ["wheat berries", "wheat"] },
    { id: 58, title: "Eat Amaranth", category: "nutrition", summary: "Ancient grain option", timing: "Allowed during diet", priority: "low", keywords: ["amaranth", "grain"] },
    { id: 59, title: "Eat Chia Seeds", category: "nutrition", summary: "Raw hemp seeds, chia seeds", timing: "Allowed during diet", priority: "low", keywords: ["chia seeds", "hemp seeds", "seeds"] },
    { id: 60, title: "Use Fresh Herbs", category: "nutrition", summary: "Basil, oregano, thyme, sage, dill", timing: "Allowed during diet", priority: "low", keywords: ["herbs", "basil", "oregano", "thyme", "sage"] },
    { id: 61, title: "Use Mild Spices", category: "nutrition", summary: "Cumin, coriander, turmeric", timing: "Allowed during diet", priority: "low", keywords: ["mild spices", "cumin", "coriander", "turmeric"] },

    // Physical Preparation
    { id: 32, title: "Fast 12+ Hours Before Ceremony", category: "physical", summary: "Empty stomach enhances effects", timing: "Day of ceremony", priority: "high", keywords: ["fast", "fasting", "empty stomach", "hungry"] },
    { id: 62, title: "Practice Fasting Monthly", category: "physical", summary: "Get used to fasting sensations", timing: "Start 4 weeks before", priority: "medium", keywords: ["practice fasting", "monthly", "ekadashi"] },
    { id: 63, title: "Consider 24-Hour Fast", category: "physical", summary: "For experienced fasters - deeper effects", timing: "Day of ceremony", priority: "low", keywords: ["24 hour fast", "advanced", "deep"] },
    { id: 33, title: "Practice Yoga Daily", category: "physical", summary: "Helps process trauma through body", timing: "Start 3 weeks before", priority: "medium", keywords: ["yoga", "stretch", "flexibility"] },
    { id: 34, title: "No Sexual Activity", category: "physical", summary: "Conserve energy for ceremony", timing: "2 weeks before", priority: "medium", keywords: ["sex", "sexual", "abstinence", "celibate"] },
    { id: 35, title: "Do Breathing Exercises", category: "physical", summary: "Practice deep, controlled breathing", timing: "Start 2 weeks before", priority: "medium", keywords: ["breathing", "breath", "breathe"] },

    // Safety & Medications  
    { id: 36, title: "Stop Antidepressants", category: "safety", summary: "SSRIs dangerous with ayahuasca", timing: "3 weeks before", priority: "critical", keywords: ["antidepressants", "SSRI", "medication", "depression"] },
    { id: 37, title: "Stop All Recreational Drugs", category: "safety", summary: "No street drugs or substances", timing: "4 weeks before", priority: "critical", keywords: ["drugs", "recreational", "street drugs"] },
    { id: 38, title: "Stop Marijuana", category: "safety", summary: "Cannabis in any form", timing: "2 weeks before", priority: "high", keywords: ["marijuana", "weed", "cannabis", "THC"] },
    { id: 39, title: "Consult Doctor About Medications", category: "safety", summary: "Review all prescriptions", timing: "4+ weeks before", priority: "critical", keywords: ["doctor", "medication", "prescription"] },
    { id: 40, title: "Avoid St. John's Wort", category: "safety", summary: "Herbal supplement interaction", timing: "2 weeks before", priority: "medium", keywords: ["st john's wort", "supplements", "herbs"] },
    { id: 64, title: "Avoid Kava Supplements", category: "safety", summary: "Herbal supplement with interactions", timing: "2 weeks before", priority: "medium", keywords: ["kava", "supplements"] },
    { id: 65, title: "Avoid Yohimbe", category: "safety", summary: "Herbal supplement with interactions", timing: "2 weeks before", priority: "medium", keywords: ["yohimbe", "supplements"] },
    { id: 66, title: "Gradually Reduce Medications", category: "safety", summary: "Don't stop abruptly - taper slowly", timing: "4+ weeks before", priority: "critical", keywords: ["gradual", "taper", "reduce", "slowly"] },

    // Integration
    { id: 41, title: "Plan Post-Retreat Time", category: "integration", summary: "Extra days for processing insights", timing: "Before booking retreat", priority: "medium", keywords: ["after", "post", "integration", "plan"] },
    { id: 42, title: "Stop Over-Researching", category: "integration", summary: "Focus on preparation, not information", timing: "After booking retreat", priority: "medium", keywords: ["research", "reading", "information"] },
    { id: 67, title: "Plan Travel After Retreat", category: "integration", summary: "Cusco, Machu Picchu, new experiences", timing: "Before booking retreat", priority: "low", keywords: ["travel", "cusco", "machu picchu", "new"] },
    { id: 68, title: "Schedule Integration Weekends", category: "integration", summary: "Hikes, walks, nearby exploration", timing: "Before booking retreat", priority: "medium", keywords: ["weekends", "hikes", "walks", "exploration"] }
  ], []); // <- Add this closing bracket and empty dependency array


  // Save to localStorage whenever bookmarks/completed change
  useEffect(() => {
    localStorage.setItem('ayahuasca-bookmarks', JSON.stringify([...bookmarkedCards]));
  }, [bookmarkedCards]);

  useEffect(() => {
    localStorage.setItem('ayahuasca-completed', JSON.stringify([...completedCards]));
  }, [completedCards]);

  // Convert timing to weeks for filtering
  const getTimingWeeks = (timing) => {
    if (timing.includes('4+') || timing.includes('Start 4')) return 4;
    if (timing.includes('3') && timing.includes('before')) return 3;
    if (timing.includes('2') && timing.includes('before')) return 2;
    if (timing.includes('1') && timing.includes('before')) return 1;
    if (timing.includes('Day of')) return 0;
    return 999; // for "allowed during diet" etc
  };

  // Filter by timeline
  const filterByTimeline = (cards, timeline) => {
    if (timeline === 'all') return cards;

    const timelineWeeks = {
      '4+': 4,
      '3+': 3,
      '2+': 2,
      '1+': 1,
      'ceremony': 0
    };

    const minWeeks = timelineWeeks[timeline];
    return cards.filter(card => {
      const cardWeeks = getTimingWeeks(card.timing);
      if (timeline === 'ceremony') return cardWeeks === 0;
      return cardWeeks >= minWeeks;
    });
  };

  // Improved search functionality
  const searchWithAI = useCallback((query) => {
    if (!query.trim()) {
      setFilteredCards([]);
      setHighlightedCards(new Set());
      return;
    }

    setIsSearching(true);

    // Use simple keyword matching for now - more reliable
    const lowerQuery = query.toLowerCase();
    const queryWords = lowerQuery.split(' ').filter(word => word.length > 2);

    const results = cards.filter(card => {
      const searchText = `${card.title} ${card.summary} ${card.keywords.join(' ')}`.toLowerCase();

      // Check for exact phrase matches first
      if (searchText.includes(lowerQuery)) {
        return true;
      }

      // Then check for individual word matches
      return queryWords.some(word => searchText.includes(word));
    });

    // Sort results by relevance
    const sortedResults = results.sort((a, b) => {
      const aText = `${a.title} ${a.summary} ${a.keywords.join(' ')}`.toLowerCase();
      const bText = `${b.title} ${b.summary} ${b.keywords.join(' ')}`.toLowerCase();

      // Exact phrase matches score highest
      const aExactMatch = aText.includes(lowerQuery) ? 1000 : 0;
      const bExactMatch = bText.includes(lowerQuery) ? 1000 : 0;

      // Count keyword matches
      const aWordMatches = queryWords.filter(word => aText.includes(word)).length;
      const bWordMatches = queryWords.filter(word => bText.includes(word)).length;

      // Title matches score higher than summary matches
      const aTitleMatch = a.title.toLowerCase().includes(lowerQuery) ? 100 : 0;
      const bTitleMatch = b.title.toLowerCase().includes(lowerQuery) ? 100 : 0;

      const aScore = aExactMatch + aWordMatches * 10 + aTitleMatch;
      const bScore = bExactMatch + bWordMatches * 10 + bTitleMatch;

      return bScore - aScore;
    });

    setFilteredCards(sortedResults);
    if (sortedResults.length > 0) {
      setHighlightedCards(new Set(sortedResults.slice(0, 3).map(card => card.id)));
      setTimeout(() => setHighlightedCards(new Set()), 3000);
    }

    setIsSearching(false);
  }, [cards]);

  useEffect(() => {
    const timeoutId = setTimeout(() => searchWithAI(searchQuery), 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchWithAI]);

  // Apply filters
  let displayCards = filteredCards.length > 0 ? filteredCards : cards;

  // Filter out completed cards from main display
  displayCards = displayCards.filter(card => !completedCards.has(card.id));

  // Apply category filter
  if (selectedCategory !== 'all') {
    displayCards = displayCards.filter(card => card.category === selectedCategory);
  }

  // Apply timeline filter  
  displayCards = filterByTimeline(displayCards, selectedTimeline);

  // Sort by priority (unless there's a search query - then keep relevance order)
  if (!searchQuery || filteredCards.length === 0) {
    const priorityOrder = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3 };
    const timingUrgencyOrder = {
      '4+ weeks before': 0,
      'Start 4 weeks before': 0,
      '3 weeks before': 1,
      'Start 3 weeks before': 1,
      '2 weeks before': 2,
      'Start 2 weeks before': 2,
      '1 week before': 3,
      'Before booking retreat': 4,
      'After booking retreat': 5,
      'Day of ceremony': 6,
      'Allowed during diet': 7
    };

    displayCards = [...displayCards].sort((a, b) => {
      // First sort by priority
      const aPriority = priorityOrder[a.priority] ?? 999;
      const bPriority = priorityOrder[b.priority] ?? 999;

      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }

      // If same priority, sort by timing urgency (earlier deadlines first)
      const aUrgency = timingUrgencyOrder[a.timing] ?? 999;
      const bUrgency = timingUrgencyOrder[b.timing] ?? 999;

      return aUrgency - bUrgency;
    });
  }

  const toggleBookmark = (cardId) => {
    const newBookmarks = new Set(bookmarkedCards);
    if (newBookmarks.has(cardId)) {
      newBookmarks.delete(cardId);
    } else {
      newBookmarks.add(cardId);
    }
    setBookmarkedCards(newBookmarks);
  };

  const toggleComplete = (cardId) => {
    const newCompleted = new Set(completedCards);
    if (newCompleted.has(cardId)) {
      newCompleted.delete(cardId);
    } else {
      newCompleted.add(cardId);
    }
    setCompletedCards(newCompleted);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'low': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-100">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Ayahuasca Preparation Guide
          </h1>
          <p className="text-gray-600">
            Specific, actionable guidance for your retreat preparation
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <div className="relative flex items-center">
              <Search className={`absolute left-4 h-5 w-5 ${isSearching ? 'text-green-500 animate-pulse' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Ask anything... (e.g., 'can I eat chicken?', 'when to stop coffee?')"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border border-gray-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Category Filters */}
        <div className="mb-6">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full transition-all ${selectedCategory === 'all'
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              All Categories ({cards.length})
            </button>
            {Object.entries(categories).map(([key, category]) => {
              const IconComponent = category.icon;
              const count = cards.filter(card => card.category === key).length;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-full transition-all flex items-center gap-2 ${selectedCategory === key
                    ? category.color.replace('100', '500').replace('800', 'white') + ' text-white'
                    : `bg-white text-gray-600 hover:${category.color.split(' ')[0]} border border-gray-200`
                    }`}
                >
                  <IconComponent className="h-4 w-4" />
                  {category.name} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Timeline Filters */}
        <div className="mb-8">
          <div className="text-center mb-4">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Filter by Timeline</h3>
            <p className="text-sm text-gray-600">Show me what to do if I have this much time before my retreat</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => setSelectedTimeline('all')}
              className={`px-4 py-2 rounded-full transition-all ${selectedTimeline === 'all'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              All Timeline
            </button>
            <button
              onClick={() => setSelectedTimeline('4+')}
              className={`px-4 py-2 rounded-full transition-all ${selectedTimeline === '4+'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              4+ Weeks Before ({filterByTimeline(cards, '4+').length})
            </button>
            <button
              onClick={() => setSelectedTimeline('3+')}
              className={`px-4 py-2 rounded-full transition-all ${selectedTimeline === '3+'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              3+ Weeks Before ({filterByTimeline(cards, '3+').length})
            </button>
            <button
              onClick={() => setSelectedTimeline('2+')}
              className={`px-4 py-2 rounded-full transition-all ${selectedTimeline === '2+'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              2+ Weeks Before ({filterByTimeline(cards, '2+').length})
            </button>
            <button
              onClick={() => setSelectedTimeline('1+')}
              className={`px-4 py-2 rounded-full transition-all ${selectedTimeline === '1+'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              1+ Week Before ({filterByTimeline(cards, '1+').length})
            </button>
            <button
              onClick={() => setSelectedTimeline('ceremony')}
              className={`px-4 py-2 rounded-full transition-all ${selectedTimeline === 'ceremony'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              Day of Ceremony ({filterByTimeline(cards, 'ceremony').length})
            </button>
          </div>
        </div>

        {/* Results Info */}
        {filteredCards.length > 0 && searchQuery && (
          <div className="mb-6 text-center">
            <p className="text-gray-600">
              Found {filteredCards.length} result{filteredCards.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          </div>
        )}

        {/* Filter Results Info */}
        {!searchQuery && (selectedCategory !== 'all' || selectedTimeline !== 'all') && (
          <div className="mb-6 text-center">
            <p className="text-gray-600">
              Showing {displayCards.length} items
              {selectedCategory !== 'all' && ` in ${categories[selectedCategory].name}`}
              {selectedTimeline !== 'all' && ` for ${selectedTimeline === 'ceremony' ? 'Day of Ceremony' : selectedTimeline + ' weeks before'}`}
            </p>
          </div>
        )}

        {/* Cards Grid - Fixed responsive layout */}
        <div className="grid gap-4" style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))'
        }}>
          {displayCards.map((card) => {
            const categoryInfo = categories[card.category];
            const IconComponent = categoryInfo.icon;
            const isHighlighted = highlightedCards.has(card.id);
            const isBookmarked = bookmarkedCards.has(card.id);
            const isCompleted = completedCards.has(card.id);

            return (
              <div
                key={card.id}
                className={`bg-white rounded-xl shadow-sm border transition-all duration-500 hover:shadow-md ${isHighlighted ? 'ring-2 ring-green-400 shadow-lg transform scale-105' : 'border-gray-200'
                  } ${isCompleted ? 'opacity-50' : ''}`}
              >
                <div className="p-4">
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleComplete(card.id)}
                        className={`transition-colors ${isCompleted
                          ? 'text-green-500'
                          : 'text-gray-400 hover:text-green-500'
                          }`}
                      >
                        {isCompleted ?
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          :
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" strokeWidth="2" />
                          </svg>
                        }
                      </button>
                      <button
                        onClick={() => toggleBookmark(card.id)}
                        className="text-gray-400 hover:text-yellow-500 transition-colors"
                      >
                        {isBookmarked ? <BookmarkCheck className="h-4 w-4 text-yellow-500" /> : <Bookmark className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-semibold text-gray-900 text-sm mb-2 leading-tight">
                    {card.title}
                  </h3>

                  {/* Summary */}
                  <p className="text-gray-600 text-xs mb-3 leading-relaxed">
                    {card.summary}
                  </p>

                  {/* Priority & Timing */}
                  <div className="space-y-2">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(card.priority)}`}>
                      {card.priority.charAt(0).toUpperCase() + card.priority.slice(1)}
                    </span>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      {card.timing}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {displayCards.length === 0 && searchQuery && !isSearching && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Search className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600">
              Try asking about specific foods, medications, or practices
            </p>
          </div>
        )}

        {/* No Results for Filters */}
        {displayCards.length === 0 && !searchQuery && (selectedCategory !== 'all' || selectedTimeline !== 'all') && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Clock className="h-12 w-12 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No items for this filter combination</h3>
            <p className="text-gray-600">
              Try adjusting your category or timeline filters
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 text-center text-gray-500 border-t border-gray-200 pt-8">
          <p className="mb-2">
            <strong>Disclaimer:</strong> This information is based on traditional practices and anecdotal evidence.
          </p>
          <p>
            Always consult with your doctor before making changes to medications or diet.
          </p>
        </div>

        {/* Completed Items Stash Box */}
        {completedCards.size > 0 && (
          <div className="fixed bottom-6 right-6">
            <button
              onClick={() => setShowCompletedStash(true)}
              className="bg-green-500 text-white rounded-full p-3 shadow-lg hover:bg-green-600 transition-all transform hover:scale-105 flex items-center gap-2"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">{completedCards.size}</span>
            </button>
          </div>
        )}

        {/* Completed Items Overlay */}
        {showCompletedStash && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => setShowCompletedStash(false)}
          >
            <div 
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[calc(100vh-2rem)] my-4 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  Completed Items ({completedCards.size})
                </h2>
                <button
                  onClick={() => setShowCompletedStash(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cards.filter(card => completedCards.has(card.id)).map((card) => {
                    const categoryInfo = categories[card.category];
                    const IconComponent = categoryInfo.icon;

                    return (
                      <div key={card.id} className="bg-green-50 rounded-xl border border-green-200 p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <button
                            onClick={() => toggleComplete(card.id)}
                            className="text-green-500 hover:text-gray-500 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                        <h3 className="font-semibold text-gray-900 text-sm mb-2 leading-tight line-through">
                          {card.title}
                        </h3>
                        <p className="text-gray-600 text-xs mb-3 leading-relaxed">
                          {card.summary}
                        </p>
                        <div className="space-y-2">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(card.priority)}`}>
                            {card.priority.charAt(0).toUpperCase() + card.priority.slice(1)}
                          </span>
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="h-3 w-3" />
                            {card.timing}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AyahuascaPrepGuide;